import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
  Shield,
  Calendar,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { siteConfig } from '../../config/site';
import api from '../../lib/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Event Quote Form Component
function EventQuoteForm() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    event_name: '',
    event_type: '',
    event_dates: '',
    event_time: '',
    event_duration: '',
    event_location: '',
    expected_attendees: '',
    coverage_level: '',
    risk_factors: [],
    additional_notes: ''
  });

  const riskFactors = [
    { id: 'alcohol', label: 'Alcohol served' },
    { id: 'motorsport', label: 'Motorsport/Racing' },
    { id: 'water', label: 'Water activities' },
    { id: 'heat', label: 'Extreme heat/cold' },
    { id: 'overnight', label: 'Overnight event' },
    { id: 'physical', label: 'Physical activity' },
    { id: 'outdoor', label: 'Outdoor venue' },
    { id: 'crowded', label: 'High crowd density' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Map form data to backend format
      const payload = {
        business_name: formData.business_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        event_location: formData.event_location,
        event_dates: `${formData.event_dates} ${formData.event_time}`.trim(),
        event_duration: formData.event_duration || '4 hours',
        expected_attendees: formData.expected_attendees,
        event_type: formData.event_type || 'other',
        coverage_level: formData.coverage_level || 'standard',
        additional_notes: `${formData.event_name ? 'Event: ' + formData.event_name + '. ' : ''}${formData.risk_factors.length ? 'Risk factors: ' + formData.risk_factors.join(', ') + '. ' : ''}${formData.additional_notes || ''}`
      };
      await api.post('/api/event-quotes', payload);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleRiskChange = (factorId, checked) => {
    setFormData(prev => ({
      ...prev,
      risk_factors: checked 
        ? [...prev.risk_factors, factorId]
        : prev.risk_factors.filter(f => f !== factorId)
    }));
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-8 rounded-3xl bg-slate-900/50 border border-emerald-500/30 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">Request Submitted!</h3>
        <p className="text-slate-400 mb-6">
          Thank you for your interest in Apex Medical Group. Our team will review your request 
          and contact you within 24 hours with a customized quote.
        </p>
        <Button 
          onClick={() => setSubmitted(false)}
          variant="outline"
          className="border-slate-700 text-white hover:bg-white/10 rounded-full"
        >
          Submit Another Request
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Business Name *</Label>
          <Input
            required
            value={formData.business_name}
            onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="Your company name"
            data-testid="quote-business-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Contact Person *</Label>
          <Input
            required
            value={formData.contact_name}
            onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="Your full name"
            data-testid="quote-contact-name"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Email *</Label>
          <Input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="email@company.com"
            data-testid="quote-email"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Phone *</Label>
          <Input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="+1 (758) 000-0000"
            data-testid="quote-phone"
          />
        </div>
      </div>

      <div className="border-t border-slate-800 pt-6">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-400" />
          Event Details
        </h4>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Event Name *</Label>
          <Input
            required
            value={formData.event_name}
            onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="Annual Company Picnic"
            data-testid="quote-event-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Event Type</Label>
          <Select
            value={formData.event_type}
            onValueChange={(v) => setFormData(prev => ({ ...prev, event_type: v }))}
          >
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="quote-event-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="festival">Festival / Concert</SelectItem>
              <SelectItem value="sports">Sports Event</SelectItem>
              <SelectItem value="corporate">Corporate Event</SelectItem>
              <SelectItem value="private">Private Party</SelectItem>
              <SelectItem value="wedding">Wedding</SelectItem>
              <SelectItem value="film">Film/TV Production</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Event Date *</Label>
          <Input
            type="date"
            required
            value={formData.event_dates}
            onChange={(e) => setFormData(prev => ({ ...prev, event_dates: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white"
            min={new Date().toISOString().split('T')[0]}
            data-testid="quote-event-date"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Start Time</Label>
          <Input
            type="time"
            value={formData.event_time}
            onChange={(e) => setFormData(prev => ({ ...prev, event_time: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white"
            data-testid="quote-event-time"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Duration (hours)</Label>
          <Input
            type="number"
            value={formData.event_duration}
            onChange={(e) => setFormData(prev => ({ ...prev, event_duration: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white"
            placeholder="4"
            min="1"
            data-testid="quote-event-duration"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Location *</Label>
          <Input
            required
            value={formData.event_location}
            onChange={(e) => setFormData(prev => ({ ...prev, event_location: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="Venue name and address"
            data-testid="quote-location"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Expected Attendance *</Label>
          <Select
            value={formData.expected_attendees}
            onValueChange={(v) => setFormData(prev => ({ ...prev, expected_attendees: v }))}
          >
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="quote-attendance">
              <SelectValue placeholder="Select attendance" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="under500">Under 500</SelectItem>
              <SelectItem value="500">500</SelectItem>
              <SelectItem value="1000">1,000</SelectItem>
              <SelectItem value="2000">2,000</SelectItem>
              <SelectItem value="5000">5,000</SelectItem>
              <SelectItem value="10000">10,000</SelectItem>
              <SelectItem value="20000">20,000+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white">Coverage Level *</Label>
        <Select
          value={formData.coverage_level}
          onValueChange={(v) => setFormData(prev => ({ ...prev, coverage_level: v }))}
        >
          <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="quote-coverage">
            <SelectValue placeholder="Select coverage level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">Basic - First Aid Station</SelectItem>
            <SelectItem value="standard">Standard - First Aid + Roaming EMTs</SelectItem>
            <SelectItem value="enhanced">Enhanced - ALS Coverage + Physician</SelectItem>
            <SelectItem value="premium">Premium - Full Medical Team + Ambulance Standby</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <Label className="text-white flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Risk Factors (check all that apply)
        </Label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {riskFactors.map((factor) => (
            <div key={factor.id} className="flex items-center gap-2">
              <Checkbox
                id={factor.id}
                checked={formData.risk_factors.includes(factor.id)}
                onCheckedChange={(checked) => handleRiskChange(factor.id, checked)}
                className="border-slate-600 data-[state=checked]:bg-emerald-500"
              />
              <label htmlFor={factor.id} className="text-slate-300 text-sm cursor-pointer">
                {factor.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white">Additional Notes</Label>
        <Textarea
          value={formData.additional_notes}
          onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))}
          className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 min-h-[100px]"
          placeholder="Any additional details about your event or specific requirements..."
          data-testid="quote-notes"
        />
      </div>

      <Button 
        type="submit"
        size="lg"
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
        disabled={loading}
        data-testid="quote-submit-btn"
      >
        {loading ? 'Submitting...' : 'Submit Quote Request'}
        <Send className="ml-2 w-5 h-5" />
      </Button>
    </form>
  );
}

// General Contact Form
function GeneralContactForm() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setLoading(false);
    toast.success('Message sent successfully!');
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-8 rounded-3xl bg-slate-900/50 border border-emerald-500/30 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
        <p className="text-slate-400 mb-6">
          Thank you for contacting us. We&apos;ll get back to you within 24 hours.
        </p>
        <Button 
          onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
          variant="outline"
          className="border-slate-700 text-white hover:bg-white/10 rounded-full"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Name *</Label>
          <Input
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="Your full name"
            data-testid="contact-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Email *</Label>
          <Input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="email@example.com"
            data-testid="contact-email"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white">Phone</Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="+1 (758) 000-0000"
            data-testid="contact-phone"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Subject *</Label>
          <Select
            value={formData.subject}
            onValueChange={(v) => setFormData(prev => ({ ...prev, subject: v }))}
          >
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="contact-subject">
              <SelectValue placeholder="Select subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General Inquiry</SelectItem>
              <SelectItem value="event">Event Coverage</SelectItem>
              <SelectItem value="home-visit">Home Consultation</SelectItem>
              <SelectItem value="training">Training Programs</SelectItem>
              <SelectItem value="partnership">Partnership</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white">Message *</Label>
        <Textarea
          required
          value={formData.message}
          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
          className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 min-h-[150px]"
          placeholder="How can we help you?"
          data-testid="contact-message"
        />
      </div>

      <Button 
        type="submit"
        size="lg"
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
        disabled={loading}
        data-testid="contact-submit-btn"
      >
        {loading ? 'Sending...' : 'Send Message'}
        <Send className="ml-2 w-5 h-5" />
      </Button>
    </form>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="pt-32 pb-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-emerald-400 font-medium mb-4">
            Contact Us
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Let's
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400"> Talk</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400 leading-relaxed">
            Ready to discuss your medical coverage needs? Request a quote, book a consultation, 
            or simply get in touch with our team.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// Main Contact Section
function ContactSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [searchParams] = useSearchParams();
  const formType = searchParams.get('type');

  const [activeForm, setActiveForm] = useState(formType === 'event-quote' ? 'quote' : 'contact');

  return (
    <section ref={ref} className="py-16 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid lg:grid-cols-3 gap-12"
        >
          {/* Contact Info */}
          <motion.div variants={fadeInUp} className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Get in Touch</h3>
              <p className="text-slate-400">
                Have questions? Our team is here to help with any inquiries about our services.
              </p>
            </div>

            <div className="space-y-6">
              <a 
                href={siteConfig.contact.phone.href}
                className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-emerald-500/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                  <Phone className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Call us</p>
                  <p className="text-white font-medium">{siteConfig.contact.phone.display}</p>
                </div>
              </a>

              <a 
                href={siteConfig.contact.email.href}
                className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-emerald-500/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email us</p>
                  <p className="text-white font-medium">{siteConfig.contact.email.primary}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Visit us</p>
                  <p className="text-white font-medium">{siteConfig.address.full}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Business Hours</p>
                  <p className="text-white font-medium">
                    {siteConfig.hours.display}<br />
                    <span className="text-emerald-400 text-sm">{siteConfig.hours.note}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <a
              href={siteConfig.contact.whatsapp.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 p-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
              data-testid="whatsapp-btn"
            >
              <MessageSquare className="w-5 h-5" />
              {siteConfig.contact.whatsapp.display}
            </a>
          </motion.div>

          {/* Form Section */}
          <motion.div variants={fadeInUp} className="lg:col-span-2">
            {/* Form Toggle */}
            <div className="flex gap-2 mb-8">
              <Button
                variant={activeForm === 'contact' ? 'default' : 'outline'}
                onClick={() => setActiveForm('contact')}
                className={`flex-1 rounded-full ${activeForm === 'contact' ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-white hover:bg-white/10'}`}
                data-testid="toggle-contact-form"
              >
                <Mail className="w-4 h-4 mr-2" />
                General Inquiry
              </Button>
              <Button
                variant={activeForm === 'quote' ? 'default' : 'outline'}
                onClick={() => setActiveForm('quote')}
                className={`flex-1 rounded-full ${activeForm === 'quote' ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-white hover:bg-white/10'}`}
                data-testid="toggle-quote-form"
              >
                <Shield className="w-4 h-4 mr-2" />
                Event Quote Request
              </Button>
            </div>

            <div className="p-8 rounded-3xl bg-slate-900/50 border border-slate-800">
              <h3 className="text-2xl font-bold text-white mb-2">
                {activeForm === 'quote' ? 'Request Event Quote' : 'Send Us a Message'}
              </h3>
              <p className="text-slate-400 mb-6">
                {activeForm === 'quote' 
                  ? 'Fill out the form below and our team will prepare a customized quote for your event.'
                  : 'Have a question? Fill out the form and we&apos;ll get back to you within 24 hours.'}
              </p>
              
              {activeForm === 'quote' ? <EventQuoteForm /> : <GeneralContactForm />}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Map Section (placeholder)
function MapSection() {
  return (
    <section className="py-16 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-96 rounded-3xl bg-slate-800 border border-slate-700 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500">Map placeholder - Google Maps integration</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <MarketingLayout>
      <HeroSection />
      <ContactSection />
      <MapSection />
    </MarketingLayout>
  );
}
